import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { sharePointService } from '../services/sharePointService';
import { useUserSession } from './UserContext';
import authApi from '../services/authApi';
import { siteStatsDebug } from '../utils/siteStatsDebug';

const SharePointContext = createContext();
const STORAGE_KEY = 'securepoint_selected_config';

function readPersistedConfigId() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) return null;
  const n = parseInt(saved, 10);
  return Number.isNaN(n) ? null : n;
}

export const SharePointProvider = ({ children }) => {
  const { userSession, loading: authLoading } = useUserSession();
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(() => {
    const configId = readPersistedConfigId();
    console.log('Initializing selectedConfig from localStorage:', configId);
    return configId;
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pendingSelectConfigIdRef = useRef(null);

  /** JWT impersonation config when set; otherwise UI/localStorage selected site. */
  const effectiveConfigId = useMemo(() => {
    const imp = userSession?.impersonatedConfigId;
    if (imp != null && !Number.isNaN(parseInt(imp, 10))) {
      return parseInt(imp, 10);
    }
    return selectedConfig;
  }, [userSession?.impersonatedConfigId, selectedConfig]);

  const syncSelectedConfig = useCallback((configId) => {
    const n = parseInt(configId, 10);
    if (Number.isNaN(n) || n < 1) return;
    setSelectedConfig(n);
    localStorage.setItem(STORAGE_KEY, String(n));
  }, []);

  // Track selectedConfig changes
  useEffect(() => {
    siteStatsDebug('SharePointCtx', {
      event: 'selectedConfig_changed',
      selectedConfig,
      refreshTrigger,
      pendingSelectConfigId: pendingSelectConfigIdRef.current,
      sessionCfg: userSession?.impersonatedConfigId ?? null,
    });
  }, [selectedConfig, configurations, refreshTrigger, userSession?.impersonatedConfigId]);

  useEffect(() => {
    const fetchConfigurations = async () => {
      setLoading(true);
      try {
        if (userSession?.email) {
          const token = authApi.getToken();
          const response = await sharePointService.getConfig(token);
          
          if (response && response.success && response.all_configurations) {
            console.log('SharePoint configurations loaded in context:', response.all_configurations);

            const accessibleConfigs = response.all_configurations.filter(config =>
              config.id && config.tenant_url
            );

            setConfigurations(accessibleConfigs);

            if (accessibleConfigs.length > 0) {
              const want = pendingSelectConfigIdRef.current;
              const sessionCfg =
                userSession?.impersonatedConfigId != null &&
                !Number.isNaN(parseInt(userSession.impersonatedConfigId, 10))
                  ? parseInt(userSession.impersonatedConfigId, 10)
                  : null;
              const persistedConfigId = readPersistedConfigId();
              const currentSelected = selectedConfig ?? persistedConfigId;

              console.log('Resolving selectedConfig:', {
                state: selectedConfig,
                persisted: persistedConfigId,
                want,
                sessionCfg,
              });

              if (want != null && accessibleConfigs.some((c) => c.id === want)) {
                setSelectedConfig(want);
                localStorage.setItem(STORAGE_KEY, String(want));
                pendingSelectConfigIdRef.current = null;
              } else if (
                sessionCfg != null &&
                accessibleConfigs.some((c) => c.id === sessionCfg)
              ) {
                setSelectedConfig(sessionCfg);
                localStorage.setItem(STORAGE_KEY, String(sessionCfg));
                pendingSelectConfigIdRef.current = null;
              } else {
                pendingSelectConfigIdRef.current = null;
                const configExists = accessibleConfigs.some(
                  (config) => config.id === currentSelected
                );

                if (!currentSelected || !configExists) {
                  const newSelectedConfig = accessibleConfigs[0].id;
                  setSelectedConfig(newSelectedConfig);
                  localStorage.setItem(STORAGE_KEY, String(newSelectedConfig));
                } else if (currentSelected !== selectedConfig) {
                  setSelectedConfig(currentSelected);
                }
              }

              siteStatsDebug('SharePointCtx', {
                event: 'fetchConfigurations_resolved',
                selectedConfigAfter: want ?? sessionCfg ?? currentSelected,
                want,
                sessionCfg,
                persistedConfigId,
                refreshTrigger,
                configCount: accessibleConfigs.length,
              });
            } else {
              // If no accessible configurations, clear the selected config
              setSelectedConfig(null);
              localStorage.removeItem(STORAGE_KEY);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching SharePoint configurations:', error);
        setConfigurations([]);
        setSelectedConfig(null);
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setLoading(false);
      }
    };

    if (authLoading) {
      return;
    }

    if (userSession?.email) {
      fetchConfigurations();
    } else {
      setLoading(false);
      setConfigurations([]);
      setSelectedConfig(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [userSession?.email, userSession?.effectiveEmail, userSession?.impersonatedConfigId, refreshTrigger, authLoading]);

  const changeSelectedConfig = (configId) => {
    console.log(`changeSelectedConfig called with configId: ${configId}, current selectedConfig: ${selectedConfig}`);
    console.log('Available configurations:', configurations.map(c => ({ id: c.id, tenant_url: c.tenant_url })));
    
    if (configId !== selectedConfig) {
      // Verify the config exists in accessible configurations
      const configExists = configurations.some(config => config.id === configId);
      console.log(`Config ${configId} exists in accessible configs:`, configExists);
      
      if (configExists) {
        console.log(`Changing selected config from ${selectedConfig} to ${configId}`);
        setSelectedConfig(configId);
        localStorage.setItem(STORAGE_KEY, String(configId));
        setRefreshTrigger((prev) => prev + 1);
      } else {
        console.warn(`Attempted to select inaccessible configuration ID: ${configId}`);
        console.warn('Available config IDs:', configurations.map(c => c.id));
      }
    } else {
      console.log('Config ID is the same as current selected config, no change needed');
    }
  };

  // Get the currently selected configuration object
  const getSelectedConfigObject = () => {
    return configurations.find(config => config.id === selectedConfig) || null;
  };

  const bumpConfigRefresh = (selectConfigIdAfterLoad) => {
    if (selectConfigIdAfterLoad != null) {
      const n = parseInt(selectConfigIdAfterLoad, 10);
      if (!Number.isNaN(n) && n > 0) {
        pendingSelectConfigIdRef.current = n;
      }
    }
    setRefreshTrigger((p) => {
      const next = p + 1;
      siteStatsDebug('SharePointCtx', {
        event: 'bumpConfigRefresh',
        selectConfigIdAfterLoad: selectConfigIdAfterLoad ?? null,
        pendingSelectConfigId: pendingSelectConfigIdRef.current,
        refreshTrigger: next,
      });
      return next;
    });
  };

  return (
    <SharePointContext.Provider 
      value={{ 
        configurations, 
        loading, 
        selectedConfig,
        effectiveConfigId,
        syncSelectedConfig,
        changeSelectedConfig,
        selectedConfigObject: getSelectedConfigObject(),
        refreshTrigger,
        bumpConfigRefresh
      }}
    >
      {children}
    </SharePointContext.Provider>
  );
};

export const useSharePoint = () => {
  const context = useContext(SharePointContext);
  if (!context) {
    throw new Error('useSharePoint must be used within a SharePointProvider');
  }
  return context;
}; 